/**
 * houseLock — device-session state for unlocked houses.
 *
 * When a house has a PIN set, its members, events and check-ins are
 * write-gated on the server. To avoid re-prompting for the PIN on
 * every tap, we cache successful unlocks in sessionStorage (so they
 * clear when the browser tab closes — by design). The PIN itself is
 * stored here; we need to re-send it with each gated write.
 *
 * sessionStorage, NOT localStorage, because:
 *  - "Lock when the iPad goes to sleep" is a property people expect.
 *  - Closing the tab = "I'm done with this family iPad for now".
 *  - localStorage would let a left-behind tab stay unlocked forever.
 */
const KEY = "famfeel_house_unlocks_v1";

function readAll() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(obj) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(obj));
    window.dispatchEvent(new CustomEvent("famfeel:house-unlocks-changed"));
  } catch {
    /* noop — private mode quota */
  }
}

/** Returns the cached PIN for a house, or null if not unlocked. */
export function getHouseUnlockPin(houseId) {
  if (!houseId) return null;
  const all = readAll();
  return all[houseId] || null;
}

export function isHouseUnlocked(houseId) {
  return !!getHouseUnlockPin(houseId);
}

export function setHouseUnlock(houseId, pin) {
  if (!houseId || !pin) return;
  const all = readAll();
  all[houseId] = pin;
  writeAll(all);
}

export function clearHouseUnlock(houseId) {
  const all = readAll();
  delete all[houseId];
  writeAll(all);
}

export function clearAllHouseUnlocks() {
  writeAll({});
}
